import { useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';

/**
 * CashFlowChart Component
 * Displays cash flow projection with toggle for 7/14/30 days
 */
export default function CashFlowChart({ data, loading = false }) {
  const [selectedPeriod, setSelectedPeriod] = useState(30);

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-4" />
          <div className="h-64 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { daily, summary, alertaNegativa, moneda = 'CRC' } = data;

  // Filter data by selected period
  const filteredData = daily?.slice(0, selectedPeriod + 1) || [];

  // Get summary for selected period
  const periodSummary = selectedPeriod === 7
    ? summary.total7d
    : selectedPeriod === 14
      ? summary.total14d
      : summary.total30d;

  // Format currency for axis (short format)
  const formatCurrency = (value) => {
    const prefix = moneda === 'USD' ? '$' : '₡';
    if (Math.abs(value) >= 1000000) {
      return `${prefix}${(value / 1000000).toFixed(1)}M`;
    }
    if (Math.abs(value) >= 1000) {
      return `${prefix}${(value / 1000).toFixed(0)}K`;
    }
    return `${prefix}${value}`;
  };

  // Format full currency for display
  const formatFullCurrency = (value) => {
    if (moneda === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    }
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
          <p className="font-medium text-gray-900 mb-2">Dia {data.dia}</p>
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-4">
              <span className="text-emerald-600">Ingresos:</span>
              <span className="font-medium">{formatFullCurrency(data.ingresos)}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-red-600">Egresos:</span>
              <span className="font-medium">{formatFullCurrency(data.egresos)}</span>
            </div>
            <div className="flex items-center justify-between gap-4 pt-1 border-t border-gray-100">
              <span className="text-gray-600">Balance:</span>
              <span className={`font-bold ${data.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {formatFullCurrency(data.balance)}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Proyeccion de Flujo de Caja</h2>
          <p className="text-xs text-gray-500">Basado en CxC y CxP pendientes</p>
        </div>

        {/* Period Toggle */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {[7, 14, 30].map((period) => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                selectedPeriod === period
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {period}d
            </button>
          ))}
        </div>
      </div>

      {/* Alert if negative balance projected */}
      {alertaNegativa && alertaNegativa.dia <= selectedPeriod && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <p className="text-xs text-amber-800">
            <span className="font-medium">Alerta:</span> Se proyecta flujo negativo en {alertaNegativa.dia} dias
            ({formatFullCurrency(Math.abs(alertaNegativa.monto))})
          </p>
        </div>
      )}

      {/* Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={filteredData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorIngresos" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorEgresos" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="dia"
              stroke="#9ca3af"
              fontSize={10}
              tickLine={false}
              axisLine={false}
              tickFormatter={(val) => `D${val}`}
            />
            <YAxis
              stroke="#9ca3af"
              fontSize={10}
              tickLine={false}
              axisLine={false}
              tickFormatter={formatCurrency}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={0} stroke="#e5e7eb" strokeDasharray="3 3" />
            <Area
              type="monotone"
              dataKey="ingresos"
              stroke="#10b981"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorIngresos)"
              name="Ingresos"
            />
            <Area
              type="monotone"
              dataKey="egresos"
              stroke="#ef4444"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorEgresos)"
              name="Egresos"
            />
            <Area
              type="monotone"
              dataKey="balance"
              stroke="#3b82f6"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorBalance)"
              name="Balance"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 mb-1">
            <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
            <span className="text-xs text-gray-500">Ingresos</span>
          </div>
          <p className="text-sm font-bold text-emerald-600">
            {formatFullCurrency(periodSummary?.ingresos || 0)}
          </p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 mb-1">
            <TrendingDown className="h-3.5 w-3.5 text-red-600" />
            <span className="text-xs text-gray-500">Egresos</span>
          </div>
          <p className="text-sm font-bold text-red-600">
            {formatFullCurrency(periodSummary?.egresos || 0)}
          </p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 mb-1">
            <span className="text-xs text-gray-500">Balance</span>
          </div>
          <p className={`text-sm font-bold ${
            (periodSummary?.balance || 0) >= 0 ? 'text-blue-600' : 'text-red-600'
          }`}>
            {formatFullCurrency(periodSummary?.balance || 0)}
          </p>
        </div>
      </div>
    </div>
  );
}
